import React, { useState } from 'react';
import { Form, Button, Alert, Container, Card } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import userService from '../services/userService';

const RequestAccountPage = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    organization: '',
    requestedRole: '',
    reason: '',
    phone: '',
  });

  const [validated, setValidated] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const form = event.currentTarget;

    setSuccessMessage('');
    setErrorMessage('');

    if (form.checkValidity() === false) {
      event.stopPropagation();
      setValidated(true);
      return;
    }

    setValidated(true);
    setSubmitting(true);

    try {
      await userService.submitAccountRequest(formData);

      setSuccessMessage('Account request submitted successfully. An administrator will review your request.');
      setFormData({
        fullName: '',
        email: '',
        organization: '',
        requestedRole: '',
        reason: '',
        phone: '',
      });
      setValidated(false);
    } catch (error) {
      console.error('RequestAccountPage: Submit failed:', error);

      const apiMessage =
        error?.response?.data?.message ||
        error?.message ||
        'Failed to submit account request.';

      setErrorMessage(apiMessage);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="login-container">
      <Container>
        <div className="d-flex justify-content-center">
          <Card className="login-form">
            <Card.Body>
              <div className="login-logo">
                <h2>Request an Account</h2>
                <p className="text-muted">Submit your information for LEADER access review</p>
              </div>

              {successMessage && (
                <Alert variant="success" dismissible onClose={() => setSuccessMessage('')}>
                  {successMessage}
                </Alert>
              )}

              {errorMessage && (
                <Alert variant="danger" dismissible onClose={() => setErrorMessage('')}>
                  {errorMessage}
                </Alert>
              )}

              <Form noValidate validated={validated} onSubmit={handleSubmit}>
                <Form.Group className="mb-3" controlId="fullName">
                  <Form.Label>Full Name</Form.Label>
                  <Form.Control
                    type="text"
                    name="fullName"
                    placeholder="Enter your full name"
                    value={formData.fullName}
                    onChange={handleChange}
                    required
                    disabled={submitting}
                  />
                  <Form.Control.Feedback type="invalid">
                    Please enter your full name.
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group className="mb-3" controlId="email">
                  <Form.Label>Work Email</Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    placeholder="Enter your work email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    disabled={submitting}
                  />
                  <Form.Control.Feedback type="invalid">
                    Please enter a valid email address.
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group className="mb-3" controlId="organization">
                  <Form.Label>Organization / Command</Form.Label>
                  <Form.Control
                    type="text"
                    name="organization"
                    placeholder="Enter your organization"
                    value={formData.organization}
                    onChange={handleChange}
                    required
                    disabled={submitting}
                  />
                  <Form.Control.Feedback type="invalid">
                    Please enter your organization.
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group className="mb-3" controlId="requestedRole">
                  <Form.Label>Requested Role</Form.Label>
                  <Form.Select
                    name="requestedRole"
                    value={formData.requestedRole}
                    onChange={handleChange}
                    required
                    disabled={submitting}
                  >
                    <option value="">Select a role...</option>
                    <option value="CONSUMER">Consumer</option>
                    <option value="PRODUCER">Producer</option>
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    Please select a requested role.
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group className="mb-3" controlId="phone">
                  <Form.Label>Phone Number (Optional)</Form.Label>
                  <Form.Control
                    type="text"
                    name="phone"
                    placeholder="Enter your phone number"
                    value={formData.phone}
                    onChange={handleChange}
                    disabled={submitting}
                  />
                </Form.Group>

                <Form.Group className="mb-3" controlId="reason">
                  <Form.Label>Reason for Access</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    name="reason"
                    placeholder="Briefly explain why you need access to LEADER"
                    value={formData.reason}
                    onChange={handleChange}
                    required
                    disabled={submitting}
                  />
                  <Form.Control.Feedback type="invalid">
                    Please provide a reason for access.
                  </Form.Control.Feedback>
                </Form.Group>

                <div className="d-grid gap-2">
                  <Button variant="primary" type="submit" disabled={submitting}>
                    {submitting ? 'Submitting...' : 'Submit Request'}
                  </Button>

                  <Button
                    variant="outline-secondary"
                    type="button"
                    onClick={() => navigate('/login')}
                    disabled={submitting}
                  >
                    Back to Login
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </div>
      </Container>
    </div>
  );
};

export default RequestAccountPage;
